import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Loader, EmptyState } from "@/components/kit";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Building2, GitBranch, ArrowLeft, ChevronRight, MapPin, Clock } from "lucide-react";

export default function CompanyDetail() {
  const { cid } = useParams();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [branches, setBranches] = useState(null);

  useEffect(() => {
    api.get(`/companies/${cid}`).then((r) => setCompany(r.data)).catch((e) => toast.error(apiError(e)));
    api.get(`/branches?company_id=${cid}`).then((r) => setBranches(r.data)).catch((e) => toast.error(apiError(e)));
  }, [cid]);

  if (!company || !branches) return <Loader />;

  return (
    <div>
      <button onClick={() => navigate("/companies")} data-testid="back-to-companies" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-navy-900 mb-4 transition-colors">
        <ArrowLeft size={15} /> Companies
      </button>

      <div className="bg-white rounded-2xl border border-slate-200 p-6 sf-fade-up">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-navy-50 text-navy-800 flex items-center justify-center"><Building2 size={26} /></div>
            <div>
              <h1 className="font-display text-2xl font-bold text-navy-900">{company.name}</h1>
              <p className="text-sm text-slate-400 font-mono">{company.code}</p>
              <p className="text-sm text-slate-500 mt-1">{company.contact_person || "—"} · {company.phone || "—"} · {company.email || "—"}</p>
            </div>
          </div>
          <StatusBadge status={company.status} />
        </div>
      </div>

      <div className="flex items-center justify-between mt-8 mb-3">
        <h2 className="font-display font-semibold text-navy-900 text-lg">Branches <span className="text-slate-400 font-normal">({branches.length})</span></h2>
      </div>

      {branches.length === 0 ? (
        <EmptyState icon={GitBranch} title="No branches yet" subtitle="This company's admin hasn't created any branches." />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {branches.map((b) => (
            <button key={b.id} onClick={() => navigate(`/branches/${b.id}`)} data-testid={`view-branch-${b.code}`}
              className="text-left bg-white rounded-2xl border border-slate-200 p-5 hover:border-navy-900 hover:shadow-md transition-all group sf-fade-up">
              <div className="flex items-start justify-between">
                <div className="w-11 h-11 rounded-xl bg-blue-50 text-blue-700 flex items-center justify-center"><GitBranch size={20} /></div>
                <StatusBadge status={b.status} />
              </div>
              <h3 className="font-display font-bold text-navy-900 mt-3 text-lg">{b.name}</h3>
              <p className="text-xs text-slate-400 font-mono">{b.code}</p>
              <p className="text-sm text-slate-500 mt-2 flex items-center gap-1"><MapPin size={13} className="shrink-0" /> {b.address || "—"}</p>
              <p className="text-xs text-slate-400 mt-1 flex items-center gap-1"><Clock size={12} /> {b.working_hours}</p>
              <span className="inline-flex items-center gap-1 text-sm font-semibold text-navy-900 mt-4 group-hover:gap-2 transition-all">View branch <ChevronRight size={15} /></span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
